import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TEN_SECONDS } from "utils";
import { Howl } from "howler";
import { api } from "utils/api";
import { Me } from "types/user";
import useLocalStorage from "hooks/useLocalStorage";

const useClientSessionPolling = () => {
  const queryClient = useQueryClient();

  const [notifications, setNotifications] = useLocalStorage(
    "notifications",
    {}
  );
  const [sessionPreferences] = useLocalStorage("session_preferences", {});

  const { data: me } = useQuery<Me>(["me"], () =>
    api.get("/users/v1/users/me")
  );
  const history = useHistory();

  const { data } = useQuery(
    ["client-nearest-sessions"],
    () => api.get(`/users/v1/my-nearest-sessions`),
    {
      refetchInterval: TEN_SECONDS,
      enabled: !!me,
      refetchIntervalInBackground: true,
      onSuccess: () => {
        queryClient.invalidateQueries(["future-sessions"], {
          refetchType: "all",
          refetchPage() {
            return true;
          },
        });
      },
    }
  );
  const [activeSessions, setActiveSessions] = useState<any>();
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    if (data) {
      const sessions = data?.sessions.filter((s) => {
        const isHidden = sessionPreferences?.[s.id]?.isNotificationHidden;

        return s.expert.id !== me?.expert?.id && !isHidden;
      });
      setActiveSessions(sessions);
      setHasActiveSession(sessions?.length >= 1);
    }
  }, [data?.sessions?.length]);

  const showActiveSession = history
    ? history?.location?.pathname?.search(/prepare|active|passed/g) === -1 ||
      !activeSessions?.find(({ id }) =>
        history?.location?.pathname.includes(id)
      )
    : true;

  useEffect(() => {
    if (hasActiveSession && showActiveSession) {
      const session = activeSessions[0];

      if (!notifications[session.id]) {
        const sound = new Howl({
          src: "/waylightnotification.mp3",
          volume: 0.3,
        });
        sound.play();
        setNotifications({ [session.id]: true });
      }
    }
  }, [hasActiveSession, showActiveSession]);

  return {
    activeSessions,
    hasActiveSession: showActiveSession && hasActiveSession,
  };
};

export default useClientSessionPolling;
